import type { AxiosError } from 'axios';
import { NoddiAsyncError } from 'noddi-types';
import { commonTexts } from '../translation/translation';
import { errorCodesEnglish } from './errorCodesEnglish';
import { mapErrorCodeToUiMessage } from './mapErrorCodeToUiMessage';

// Narrows to AxiosError, not NoddiAsyncError — the `isAxiosError` flag only
// proves it's an axios error, not that the response body follows Noddi's
// `{ errors: [...] }` shape. Callers that read `response.data.errors` should
// use a typed wrapper (e.g. treat as NoddiAsyncError) only when the request
// is known to target our API.
export const isAxiosError = (error: unknown): error is AxiosError =>
  typeof error === 'object' && error !== null && (error as { isAxiosError?: boolean }).isAxiosError === true;

/**
 * Expired or revoked session (401). Never toast it: every in-flight and refetched request fails with it at once,
 * so a toast per failure buries the screen. Sign-in is the only recovery, and each app owns that redirect.
 */
export const isSessionExpiredError = (error: unknown): boolean => isAxiosError(error) && error.status === 401;

const getErrorDetails = (error: NoddiAsyncError): string =>
  error.response?.data.errors?.map((e) => e.detail).join(', ') ?? error.message;

const getRawNoddiApiErrors = (error: NoddiAsyncError | (NoddiAsyncError | null)[]): string => {
  const nonNullErrors = Array.isArray(error) ? error.filter((e): e is NoddiAsyncError => e != null) : [error];

  return nonNullErrors.map(getErrorDetails).join(', ');
};

const narrowToNoddiAsyncErrors = (
  error: unknown
): { ok: true; error: NoddiAsyncError | (NoddiAsyncError | null)[] } | { ok: false } => {
  if (error == null) {
    return { ok: false };
  }
  if (Array.isArray(error)) {
    return { ok: true, error };
  }
  if (isAxiosError(error)) {
    // API failures are axios-shaped; downstream helpers expect `NoddiAsyncError`.
    return { ok: true, error: error as NoddiAsyncError };
  }
  return { ok: false };
};

const getInternalErrorMessageNormalized = (error: NoddiAsyncError | (NoddiAsyncError | null)[]): string => {
  const mappedUiMessage = mapErrorCodeToUiMessage({ error });
  const rawErrorMessage = getRawNoddiApiErrors(error);

  if (mappedUiMessage !== errorCodesEnglish.genericErrorMessage) {
    return mappedUiMessage;
  }

  // Returned as-is. Every caller already frames it as an error — a red toast, a
  // banner with an error icon, an error page — and the mobile toast adds its own
  // "Error" title, so a prefix here only ever read as "Error : Error".
  if (rawErrorMessage) {
    return rawErrorMessage;
  }

  return commonTexts.errors.description;
};

/**
 * Use in internal apps only, shows backend error messages directly in the UI if we don't have a mapping for error codes.
 */
export const getInternalErrorMessage = (error: unknown): string => {
  const narrowed = narrowToNoddiAsyncErrors(error);
  if (!narrowed.ok) {
    return commonTexts.errors.description;
  }
  return getInternalErrorMessageNormalized(narrowed.error);
};

/** Extract the first API error code; gateways may return a non-API body. */
export const getFirstErrorCode = (error: AxiosError | (AxiosError | null)[]): string | undefined => {
  const errors = Array.isArray(error) ? error : [error];
  for (const entry of errors) {
    const data = entry?.response?.data;
    if (typeof data !== 'object' || data === null || !('errors' in data) || !Array.isArray(data.errors)) {
      continue;
    }
    const firstError: unknown = data.errors[0];
    if (
      typeof firstError === 'object' &&
      firstError !== null &&
      'code' in firstError &&
      typeof firstError.code === 'string' &&
      firstError.code
    ) {
      return firstError.code;
    }
  }
  return undefined;
};

export const hasErrorCode = (
  error: NoddiAsyncError | (NoddiAsyncError | null)[],
  code: errorCodesEnglishKeys
): boolean => {
  const nonNullErrors = Array.isArray(error) ? error.filter((e): e is NoddiAsyncError => e != null) : [error];
  return nonNullErrors.some((e) => e.response?.data.errors?.some((err) => err.code === code) ?? false);
};

/** Extract the redirect_url from the error response, if present. */
export const getErrorRedirectUrl = (error: NoddiAsyncError | (NoddiAsyncError | null)[]): string | null => {
  const nonNullErrors = Array.isArray(error) ? error.filter((e): e is NoddiAsyncError => e != null) : [error];
  for (const e of nonNullErrors) {
    const redirectUrl = e.response?.data.redirect_url;
    if (redirectUrl) {
      return redirectUrl;
    }
  }
  return null;
};

export type errorCodesEnglishKeys = keyof typeof errorCodesEnglish;
